import React from 'react'
import { useRouter } from 'next/router'
import { LinkButton } from '@/atoms/Button'
import { InternalLink } from '@/atoms/InternalLink'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionMD, ParagraphMD, TitleLG, TitleSM } from '@/atoms/Text'
import { Episode, GetEpisodeResponse } from '@/services/ProjectsService'
import { useLoginUrl, useSignupUrl } from '@/utils/auth-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface Props {
  episode: Episode
  project: GetEpisodeResponse['project']
}

export const AccountRequiredOverlay: React.FC<Props> = ({ episode, project }) => {
  const router = useRouter()
  const { signupUrl, trackSignupStarted } = useSignupUrl(router.asPath)
  const { loginUrl, trackLoginStarted } = useLoginUrl(router.asPath)
  const { t } = useTranslate('watch')

  return (
    <div className="absolute inset-x-0 top-0 z-40 flex min-h-full justify-center sm:items-center">
      <ResponsiveBackgroundImage
        src={episode?.posterLandscapeCloudinaryPath ?? episode?.posterCloudinaryPath}
        transforms="e_blur:400,c_limit,h_600,w_600"
      />
      <div className="relative flex w-full flex-col items-center gap-6 bg-white/60 px-4 py-10 text-center text-core-gray-950 sm:mx-4 sm:max-w-screen-sm sm:rounded-2xl lg:justify-center">
        <div className="">
          {project && project.name !== episode.subtitle && (
            <TitleSM weight="normal" className="mb-1">
              {project.name}
              {project.projectType === 'series' && episode?.name && `: ${episode.name}`}
            </TitleSM>
          )}
          <TitleLG weight="semibold">{episode.subtitle}</TitleLG>
        </div>
        <ParagraphMD>
          {t('continueWatchingForFreeLogIn', 'To continue watching for free, please log in or create a new account.')}
        </ParagraphMD>
        <div>
          <LinkButton
            className="mb-4"
            href={signupUrl}
            onClick={() => trackSignupStarted('CollectiblesPlayer')}
            variant="black"
          >
            {t('signupToWatchFree', 'Sign-up to Watch Free')}
          </LinkButton>
          <CaptionMD weight="normal">
            <Translate i18nKey="alreadyHaveAnAccoutLogInv2" t={t}>
              Already have an account?{' '}
              <InternalLink
                className="cursor-pointer font-semibold underline"
                href={loginUrl}
                onClick={() => trackLoginStarted('CollectiblesPlayer.AccountRequiredOverlay')}
              >
                Log In
              </InternalLink>
            </Translate>
          </CaptionMD>
        </div>
      </div>
    </div>
  )
}
